import { useState, useEffect } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { reviewQueueService, type ResponseReview, type ReviewUpdateData } from '@/services/review-queue-service';
import markdownRenderers from '@/utils/markdown-renderers';
import {
  Header,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, CheckCircle, Eye, TestTube } from '@/lib/icons';
import { format } from 'date-fns';

type FilterStatus = '' | 'pending' | 'reviewed' | 'resolved' | 'dismissed';
type FilterSeverity = '' | 'low' | 'medium' | 'high';
type FilterCategory = '' | 'accuracy' | 'relevance' | 'tone' | 'terminology' | 'other';

export function KaiyaReviewQueuePage() {
  const { setShowPageHeader } = usePageContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('pending');
  const [filterSeverity, setFilterSeverity] = useState<FilterSeverity>('');
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('');
  const [selectedReview, setSelectedReview] = useState<ResponseReview | null>(null);
  const [editForm, setEditForm] = useState<ReviewUpdateData>({});

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data, isLoading } = useQuery({
    queryKey: ['responseReviews', filterStatus, filterSeverity, filterCategory],
    queryFn: () =>
      reviewQueueService.list({
        status: filterStatus || undefined,
        severity: filterSeverity || undefined,
        category: filterCategory || undefined,
      }),
  });

  const reviews: ResponseReview[] = extractAttributesList(data);

  const syncMutation = useMutation({
    mutationFn: () => reviewQueueService.sync(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['responseReviews'] });
      toast.success(`Synced ${result.synced_count ?? 0} new flagged responses`);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to sync'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReviewUpdateData }) =>
      reviewQueueService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['responseReviews'] });
      toast.success('Review updated');
      setSelectedReview(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update'),
  });

  const convertMutation = useMutation({
    mutationFn: (id: string) => reviewQueueService.convertToEval(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['responseReviews'] });
      toast.success('Converted to eval case', {
        action: {
          label: 'View in Evals',
          onClick: () => navigate('/kaiya-evals'),
        },
      });
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to convert'),
  });

  const openReview = (review: ResponseReview) => {
    setSelectedReview(review);
    setEditForm({
      review_status: review.review_status,
      review_category: review.review_category ?? undefined,
      severity: review.severity ?? undefined,
      reviewer_notes: review.reviewer_notes ?? '',
    });
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <Badge variant="secondary">Pending</Badge>;
      case 'reviewed': return <Badge variant="default">Reviewed</Badge>;
      case 'resolved': return <Badge variant="default">Resolved</Badge>;
      case 'dismissed': return <Badge variant="outline">Dismissed</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const severityBadge = (severity: string | null) => {
    if (!severity) return null;
    switch (severity) {
      case 'high': return <Badge variant="destructive">High</Badge>;
      case 'medium': return <Badge variant="secondary">Medium</Badge>;
      case 'low': return <Badge variant="outline">Low</Badge>;
      default: return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const pendingCount = reviews.filter(r => r.review_status === 'pending').length;

  return (
    <>
      <Header
        title="Response Review Queue"
        description="Review flagged AI responses and convert to eval cases"
      >
        <Button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {syncMutation.isPending ? 'Syncing...' : 'Sync New'}
        </Button>
      </Header>

      <ContentContainer>
        {/* Filter bar */}
        <div className="flex gap-3 mb-4">
          <Select
            value={filterStatus || 'all'}
            onValueChange={(v) => setFilterStatus((v === 'all' ? '' : v) as FilterStatus)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filterSeverity || 'all'}
            onValueChange={(v) => setFilterSeverity((v === 'all' ? '' : v) as FilterSeverity)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filterCategory || 'all'}
            onValueChange={(v) => setFilterCategory((v === 'all' ? '' : v) as FilterCategory)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="accuracy">Accuracy</SelectItem>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="tone">Tone</SelectItem>
              <SelectItem value="terminology">Terminology</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          {filterStatus === 'pending' && pendingCount > 0 && (
            <div className="flex items-center text-sm text-muted-foreground">
              {pendingCount} pending review{pendingCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {isLoading ? (
          <Empty><Spinner /></Empty>
        ) : reviews.length === 0 ? (
          <Empty>
            <EmptyIcon><CheckCircle className="h-12 w-12" /></EmptyIcon>
            <EmptyTitle>No reviews found</EmptyTitle>
            <EmptyDescription>
              {filterStatus === 'pending'
                ? 'No pending reviews. Click "Sync New" to pull recently flagged responses.'
                : 'No reviews match the current filters.'}
            </EmptyDescription>
          </Empty>
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <Card key={review.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* User message */}
                      {review.user_message && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-muted-foreground">User:</span>
                          <p className="text-sm mt-0.5 line-clamp-2">{review.user_message}</p>
                        </div>
                      )}
                      {/* Assistant message */}
                      {review.assistant_message && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-muted-foreground">Assistant:</span>
                          <p className="text-sm mt-0.5 line-clamp-3 text-muted-foreground">
                            {review.assistant_message}
                          </p>
                        </div>
                      )}
                      {/* User feedback */}
                      {review.user_feedback && (
                        <div className="mt-2 p-2 bg-muted rounded-md">
                          <span className="text-xs font-medium">Feedback:</span>
                          <p className="text-sm mt-0.5">{review.user_feedback}</p>
                        </div>
                      )}
                      {/* Meta row */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {statusBadge(review.review_status)}
                        {severityBadge(review.severity)}
                        {review.review_category && (
                          <Badge variant="outline">{review.review_category}</Badge>
                        )}
                        {review.helpful_flag === false && (
                          <Badge variant="destructive">Thumbs Down</Badge>
                        )}
                        {review.converted_to_eval_case_id && (
                          <Badge variant="default">Eval Case</Badge>
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">
                          {review.created_at ? format(new Date(review.created_at), 'MMM d, yyyy h:mm a') : ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openReview(review)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {!review.converted_to_eval_case_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => convertMutation.mutate(review.id)}
                          disabled={convertMutation.isPending}
                          title="Convert to eval case"
                        >
                          <TestTube className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </ContentContainer>

      {/* Review detail / edit dialog */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="sm:!max-w-3xl">
          <DialogHeader>
            <DialogTitle>Review Response</DialogTitle>
            <DialogDescription>
              Review and categorize this flagged AI response.
            </DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Full user message */}
              {selectedReview.user_message && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">User Message</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{selectedReview.user_message}</p>
                  </CardContent>
                </Card>
              )}

              {/* Full assistant message */}
              {selectedReview.assistant_message && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Assistant Response</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none [&_*]:text-sm [&_*]:leading-relaxed [&_*:last-child]:mb-0 [&_ol]:ml-6 [&_ul]:ml-6 [&_ol]:list-decimal [&_ul]:list-disc [&_pre]:whitespace-pre-wrap">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownRenderers}
                      >
                        {selectedReview.assistant_message}
                      </ReactMarkdown>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* User feedback */}
              {selectedReview.user_feedback && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">User Feedback</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{selectedReview.user_feedback}</p>
                  </CardContent>
                </Card>
              )}

              {/* Review form fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select
                    value={editForm.review_status || 'pending'}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, review_status: v as any }))}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="dismissed">Dismissed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Severity</label>
                  <Select
                    value={editForm.severity || 'none'}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, severity: (v === 'none' ? undefined : v) as any }))}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Not set" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <Select
                  value={editForm.review_category || 'none'}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, review_category: (v === 'none' ? undefined : v) as any }))}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    <SelectItem value="accuracy">Accuracy</SelectItem>
                    <SelectItem value="relevance">Relevance</SelectItem>
                    <SelectItem value="tone">Tone</SelectItem>
                    <SelectItem value="terminology">Terminology</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Reviewer Notes</label>
                <textarea
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  rows={3}
                  value={editForm.reviewer_notes || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, reviewer_notes: e.target.value }))}
                  placeholder="Add notes about this response..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedReview && !selectedReview.converted_to_eval_case_id && (
              <Button
                variant="outline"
                onClick={() => {
                  convertMutation.mutate(selectedReview.id);
                  setSelectedReview(null);
                }}
                disabled={convertMutation.isPending}
              >
                Convert to Eval
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelectedReview(null)}>Cancel</Button>
            <Button
              onClick={() =>
                selectedReview && updateMutation.mutate({ id: selectedReview.id, data: editForm })
              }
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
